import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { GQLInformation } from 'townapps-models';
import NotFound from '../../../components/404';
import useScreenView from '../../../hooks/useScreenView';
const SponsorsApp = dynamic(() => import('../../../components/sponsorsApp'), { ssr: false });
import InfoBar from '../../../components/infoBar';
import { CategoriesInformation } from '../../../components/useful/utils';
import client from '../../../utils/apolloClient';
import dynamic from 'next/dynamic';
import useLogView from '../../../hooks/useLogView';

const MobileUseful = dynamic(() => import('../../../components/useful/mobileUseful'));
const NormalUseful = dynamic(() => import('../../../components/useful/normalUseful'));

const calcMedian = (m: any[]) => {
  const middle = Math.floor((m.length - 1) / 2);
  return m.length % 2 ? m[middle] : (m[middle] + m[middle + 1]) / 2.0;
};

const GET_LISTINGS = gql`
  query getAll($appId: String) {
    usefulInformation: information(appId: $appId) {
      id
      category
      title
      latitude
      longitude
      phone
    }
  }
`;

const GET_SLUG = gql`
  query getData($slug: String) {
    app: getApp(slug: $slug) {
      id
      name
      slug
      location {
        coordinates
      }
      sponsoredLogo
      firstSponsor
      secondSponsor
      thirdSponsor
      fourthSponsor
      fifthSponsor
      sixthSponsor
      cards {
        useful {
          title
        }
      }
    }
  }
`;

const UsefulInformation = ({ app }) => {
  if (!app) return <NotFound />;

  useLogView(undefined, app?.id, 'UsefulInfoListing');
  useScreenView(`${app.name} general listing`);

  const { data } = useQuery<{
    usefulInformation: GQLInformation[];
  }>(GET_LISTINGS, { variables: { appId: app.id } });
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>();
  const [geojson, setGeoJson] = useState(undefined);

  const getCoords = (): [number, number] => {
    return [app?.location?.coordinates[0], app?.location?.coordinates[1]] || [-6.2557848, 53.3442381];
  };

  const [viewport, setViewport] = useState({ latitude: getCoords()[1], longitude: getCoords()[0], zoom: 15.5 });

  useEffect(() => {
    if (data) {
      const validItems = data.usefulInformation.filter(i => i.latitude && i.longitude && i.category && i.title && i.id);

      const longitude = calcMedian(validItems.map(i => i.longitude).sort());
      const latitude = calcMedian(validItems.map(i => i.latitude).sort());

      setViewport({ ...viewport, longitude, latitude });

      const onlyCategories = validItems.map(u => u.category);
      const removedDuplicates = [...new Set(onlyCategories)].filter(c => c !== CategoriesInformation.IMPORTANT_NUMBERS.name);

      setCategories(removedDuplicates as any);
      const oldCategories = sessionStorage.getItem('selectedCategories');
      if (oldCategories) {
        setSelectedCategories(JSON.parse(oldCategories));
      } else {
        setSelectedCategories(removedDuplicates as any);
      }
    }
  }, [data]);

  useEffect(() => {
    if (data && data.usefulInformation) {
      let newItems = data.usefulInformation;
      if (selectedCategories && selectedCategories.length > 0) {
        newItems = newItems.filter(i => selectedCategories.includes(i.category));
      }
      setGeoJson({
        type: 'FeatureCollection',
        features: newItems.map(i => ({
          type: 'Feature',
          properties: {
            title: i.title,
            id: i.id,
            type: i.__typename,
            category: i.category,
            phone: i.phone,
          },
          geometry: {
            type: 'Point',
            coordinates: [i.longitude, i.latitude],
          },
        })),
      });
    }
  }, [selectedCategories]);

  const changeSelectedCategories = (category: string) => {
    let selected: string[] = [];
    if (selectedCategories.includes(category)) {
      selected = [...selectedCategories.filter(t => t !== category)];
    } else {
      selected = [...selectedCategories, category];
    }
    sessionStorage.setItem(`selectedCategories`, JSON.stringify(selected));
    setSelectedCategories(selected);
  };

  const getImportantNumbers = useMemo(() => {
    if (data && data?.usefulInformation) {
      return data.usefulInformation.filter(i => i.category === CategoriesInformation.IMPORTANT_NUMBERS.name);
    }
    return [];
  }, [data]);

  return (
    <>
      <div className="mt-24 mb-8">
        <InfoBar
          title="Eolas Úsáideach / Useful Information"
          subtitle={'Practical information from parking to doctors, dentists and more.'}
          image={'/useful.png'}
          noMargin
        />
      </div>
      {data && data.usefulInformation && (
        <div className="hidden w-full md:flex">
          <NormalUseful
            geojson={geojson}
            viewport={viewport}
            importantNumbers={getImportantNumbers}
            categories={categories?.sort()}
            selectedCategories={selectedCategories}
            onClickCategory={changeSelectedCategories}
          />
        </div>
      )}
      <div className="flex w-full md:hidden">
        <MobileUseful
          geojson={geojson}
          viewport={viewport}
          importantNumbers={getImportantNumbers}
          categories={categories?.sort()}
          selectedCategories={selectedCategories}
          onClickCategory={changeSelectedCategories}
        />
      </div>
      <SponsorsApp app={app} />
    </>
  );
};

UsefulInformation.getInitialProps = async ({ query }) => {
  const apolloClient = client;
  const { data } = await apolloClient.query({
    query: GET_SLUG,
    variables: {
      slug: query.slug.toLowerCase(),
    },
  });

  return {
    app: data?.app,
  };
};

export default UsefulInformation;
